import React, { useState } from 'react';
import { ChevronRight, Dices, Flag, Hand, RotateCcw, SkipForward, Undo2 } from 'lucide-react';
import { GAME_PHASES, type ClientGameView, type FlagReason, type GameIntent } from '#shared';

interface PhaseTrackerBarProps {
  view: ClientGameView;
  onDispatch: (intent: GameIntent) => void;
}

export function PhaseTrackerBar({ view, onDispatch }: PhaseTrackerBarProps) {
  const [reasonMenuOpen, setReasonMenuOpen] = useState(false);

  const mySeatId = view.viewerSeatId;
  const mySeat = mySeatId ? view.seats[mySeatId] : null;
  const activeSeat = view.seats[view.activeSeatId];
  const isMyTurn = Boolean(
    mySeatId &&
      (view.activeSeatId === mySeatId ||
        (view.settings.teamMode &&
          view.settings.simultaneousTeamTurns &&
          mySeat?.teamId &&
          mySeat.teamId === activeSeat?.teamId))
  );
  const myFlag = mySeatId ? view.flags.find((f) => f.seatId === mySeatId) : undefined;
  const openFlagsCount = view.flags.length;
  const blockPhaseAdvance = openFlagsCount > 0 && view.settings.flagGateMode === 'phase_and_turn';
  const blockPassTurn = openFlagsCount > 0;

  return (
    <div className="rounded-xl bg-slate-950/90 border border-slate-800/95 px-2.5 py-1.5 flex flex-wrap items-center justify-between gap-2 shadow-lg">
      {/* Left: Turn Counter + Phase Steps (Click on opponent's turn to toggle Stop Presets!) */}
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar max-w-full">
        <span className="px-2 py-0.5 rounded-md bg-slate-900 border border-slate-700 text-[11px] font-extrabold text-amber-300 shrink-0">
          T{view.turnNumber}
        </span>

        {view.extraTurnsQueue.length > 0 && (
          <span className="px-1.5 py-0.5 rounded bg-indigo-500/20 border border-indigo-400/60 text-indigo-200 text-[10px] font-bold shrink-0">
            +{view.extraTurnsQueue.length} Extra
          </span>
        )}

        <div className="flex items-center gap-0.5">
          {GAME_PHASES.map((p) => {
            const active = view.phase === p.id;
            const hasStopPreset = Boolean(mySeat?.stopPresets.includes(p.id));
            return (
              <button
                key={p.id}
                type="button"
                disabled={isMyTurn ? blockPhaseAdvance : !mySeatId}
                onClick={() => {
                  if (isMyTurn) {
                    onDispatch({ type: 'SET_PHASE', phase: p.id });
                  } else if (mySeat) {
                    const nextStops = hasStopPreset
                      ? mySeat.stopPresets.filter((x) => x !== p.id)
                      : [...mySeat.stopPresets, p.id];
                    onDispatch({ type: 'SET_STOP_PRESETS', phases: nextStops });
                  }
                }}
                title={
                  isMyTurn
                    ? p.label
                    : `${p.label} — Click to ${hasStopPreset ? 'remove' : 'arm'} Priority Stop Preset`
                }
                className={`relative px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${
                  active
                    ? 'bg-amber-500 text-slate-950 shadow'
                    : hasStopPreset
                    ? 'bg-cyan-500/20 border border-cyan-400/70 text-cyan-200'
                    : isMyTurn && !blockPhaseAdvance
                    ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {p.short}
                {hasStopPreset && !active && (
                  <span className="ml-0.5 inline-block w-1 h-1 rounded-full bg-cyan-300 align-middle" />
                )}
              </button>
            );
          })}
        </div>

        {/* Non-forced 1-tap Untap / Draw Suggestions for Active Player */}
        {isMyTurn && view.phase === 'untap' && (
          <button
            type="button"
            onClick={() => {
              onDispatch({ type: 'UNTAP_ALL' });
              onDispatch({ type: 'SET_PHASE', phase: 'draw' });
            }}
            className="px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-400/60 text-cyan-200 text-[10px] font-bold hover:bg-cyan-500/30 shrink-0"
          >
            Untap All →
          </button>
        )}
        {isMyTurn && view.phase === 'draw' && (
          <button
            type="button"
            onClick={() => {
              onDispatch({ type: 'DRAW_CARDS', count: 1 });
              onDispatch({ type: 'SET_PHASE', phase: 'main1' });
            }}
            className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/60 text-emerald-200 text-[10px] font-bold hover:bg-emerald-500/30 shrink-0"
          >
            Draw for Turn →
          </button>
        )}
      </div>

      {/* Center: Open Interaction Flags Resolution Strip */}
      {openFlagsCount > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {view.flags.map((f) => {
            const raiserName = view.seats[f.seatId]?.displayName ?? f.seatId;
            const iRaised = f.seatId === mySeatId;
            return (
              <div
                key={f.flagId}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-950/90 border border-amber-400 text-[10px] text-amber-200"
              >
                <Flag className="w-3 h-3 text-amber-400" />
                <span className="font-bold">
                  #{f.order} {raiserName} ({f.reason})
                </span>
                {isMyTurn && f.status === 'open' && (
                  <button
                    type="button"
                    onClick={() => onDispatch({ type: 'MARK_FLAG_SEEN', flagId: f.flagId })}
                    className="px-1.5 rounded bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 font-bold"
                  >
                    Seen
                  </button>
                )}
                {(iRaised || view.isHost || view.isJudge) && (
                  <button
                    type="button"
                    onClick={() => onDispatch({ type: 'CLEAR_FLAG', flagId: f.flagId })}
                    className="px-1.5 rounded bg-emerald-500 text-slate-950 font-bold hover:bg-emerald-400"
                  >
                    Done
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Right: Wait Flag + Undo + Dice + Next Phase / Pass Turn */}
      <div className="flex items-center gap-1.5 ml-auto">
        <button
          type="button"
          onClick={() => onDispatch({ type: 'ROLL_DIE', sides: 20 })}
          title="Roll d20"
          className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300"
        >
          <Dices className="w-3.5 h-3.5" />
        </button>

        <button
          type="button"
          onClick={() => onDispatch({ type: 'UNDO_LAST_ACTION' })}
          title="Undo your last move"
          className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300"
        >
          <Undo2 className="w-3.5 h-3.5" />
        </button>

        {view.canUndoPassTurn && (
          <button
            type="button"
            onClick={() => onDispatch({ type: 'UNDO_PASS_TURN' })}
            title="Undo Pass Turn (1 keypress)"
            className="px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 text-[10px] font-semibold text-slate-300 flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Undo Pass</span>
          </button>
        )}

        {/* Always-reachable Wait / Hold Priority Button for non-active seated players */}
        {mySeatId && !isMyTurn && (
          <div className="relative">
            {myFlag ? (
              <button
                type="button"
                onClick={() => onDispatch({ type: 'CLEAR_FLAG', flagId: myFlag.flagId })}
                className="px-3 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs flex items-center gap-1 shadow"
              >
                <Hand className="w-3.5 h-3.5" />
                <span>Done (Clear Flag)</span>
              </button>
            ) : (
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => onDispatch({ type: 'RAISE_FLAG', reason: 'respond' })}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setReasonMenuOpen((v) => !v);
                  }}
                  className="px-3 py-1 rounded-l-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs flex items-center gap-1 shadow"
                >
                  <Hand className="w-3.5 h-3.5" />
                  <span>Wait!</span>
                </button>
                <button
                  type="button"
                  aria-label="Choose flag reason"
                  onClick={() => setReasonMenuOpen((v) => !v)}
                  className="px-1.5 py-1 rounded-r-lg bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold text-xs border-l border-amber-700/50"
                >
                  ▾
                </button>
              </div>
            )}

            {reasonMenuOpen && !myFlag && (
              <div className="absolute right-0 bottom-9 z-50 w-44 rounded-xl bg-slate-950 border border-slate-700 shadow-2xl p-1 text-xs">
                {(
                  [
                    ['respond', 'Respond to Spell/Ability'],
                    ['trigger', 'Have a Trigger'],
                    ['minute', 'Need a Minute'],
                    ['judge', 'Rules / Judge Question'],
                  ] as [FlagReason, string][]
                ).map(([r, label]) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      onDispatch({ type: 'RAISE_FLAG', reason: r });
                      setReasonMenuOpen(false);
                    }}
                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-slate-800 text-slate-200"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Active Player Next Phase & Pass Turn Gate */}
        {isMyTurn && (
          <>
            <button
              type="button"
              disabled={blockPhaseAdvance}
              onClick={() => onDispatch({ type: 'ADVANCE_PHASE' })}
              className={`px-3 py-1 rounded-lg font-extrabold text-xs flex items-center gap-1 shadow ${
                blockPhaseAdvance
                  ? 'bg-slate-800 text-amber-300 border border-amber-500/50 cursor-not-allowed'
                  : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950'
              }`}
            >
              <span>{blockPhaseAdvance ? `Resolve ${openFlagsCount} Flag${openFlagsCount > 1 ? 's' : ''}` : 'Next'}</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>

            <button
              type="button"
              disabled={blockPassTurn}
              onClick={() => onDispatch({ type: 'PASS_TURN' })}
              className={`px-3 py-1 rounded-lg font-extrabold text-xs flex items-center gap-1 shadow ${
                blockPassTurn
                  ? 'bg-slate-800 text-amber-300 border border-amber-500/50 cursor-not-allowed'
                  : 'bg-amber-500 hover:bg-amber-400 text-slate-950'
              }`}
            >
              <span>{blockPassTurn ? `Resolve ${openFlagsCount}` : 'Pass Turn'}</span>
              <SkipForward className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
